import type { ThemeColor } from '@sites/site-theme/src/types';

import type { AfterViewInit, ElementRef, QueryList } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    Output,
    ViewChildren,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
    COLOR_PICKER_PRESETS,
    COLOR_PICKER_GRADIENT_PRESETS,
    TRANSPARENT_VALUE,
} from '../constants/color-picker-presets.const';
import { LowerCasePipe } from '@angular/common';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpTransparentIconComponent } from '../../../../ui/sp-transparent-icon/src/sp-transparent-icon.component';
import { TranslateModule } from '@ngx-translate/core';

/** Columns per palette row — used for ArrowUp/ArrowDown stepping. */
const GRID_COLUMNS = 7;

@Component({
    selector: 'sp-color-picker-presets',
    templateUrl: './sp-color-picker-presets.component.html',
    styleUrls: ['./sp-color-picker-presets.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpTransparentIconComponent, LowerCasePipe, TranslateModule],
})
export class SpColorPickerPresetsComponent implements AfterViewInit {
    @Input() public color!: string;
    @Input() public colorsPreset: ThemeColor[];
    @Input() public showCustom: boolean;
    @Input() public hideGradient: boolean;

    @Output() public showCustomChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public setColorEvent: EventEmitter<string> = new EventEmitter<string>();
    @Output() public setColorPresetEvent: EventEmitter<ThemeColor> = new EventEmitter<ThemeColor>();

    @ViewChildren('swatch') private swatchRefs!: QueryList<ElementRef<HTMLButtonElement>>;

    public readonly COLOR_PICKER_PRESETS: string[][] = COLOR_PICKER_PRESETS;
    public readonly COLOR_PICKER_GRADIENT_PRESETS: string[][] = COLOR_PICKER_GRADIENT_PRESETS;
    public readonly TRANSPARENT_VALUE = TRANSPARENT_VALUE;

    private readonly destroyRef = inject(DestroyRef);

    private activeIndex = 0;

    public ngAfterViewInit(): void {
        this.initRovingTabindex();
        this.swatchRefs.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.initRovingTabindex());
    }

    /** Arrow-key navigation across the whole swatch grid (single tab stop, roving tabindex). */
    public onKeydown(event: KeyboardEvent): void {
        const els = this.swatchElements();
        const last = els.length - 1;
        if (last < 0) {
            return;
        }

        const current = els.indexOf(document.activeElement as HTMLButtonElement);
        if (current < 0) {
            return;
        }

        let next: number;
        switch (event.key) {
            case 'ArrowRight':
                next = current >= last ? 0 : current + 1;
                break;
            case 'ArrowLeft':
                next = current <= 0 ? last : current - 1;
                break;
            case 'ArrowDown':
                next = Math.min(current + GRID_COLUMNS, last);
                break;
            case 'ArrowUp':
                next = Math.max(current - GRID_COLUMNS, 0);
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = last;
                break;
            default:
                return;
        }

        event.preventDefault();
        this.setActive(next, els);
        els[next].focus();
    }

    public toggleShowCustom(event: Event): void {
        event.preventDefault();
        this.showCustomChange.emit(true);
    }

    public setColorHandler(color: string): void {
        this.setColorEvent.emit(color);
    }

    public setColorPresetHandler(preset: ThemeColor): void {
        this.setColorPresetEvent.emit(preset);
    }

    private swatchElements(): HTMLButtonElement[] {
        return this.swatchRefs?.toArray().map((ref) => ref.nativeElement) ?? [];
    }

    private initRovingTabindex(): void {
        const els = this.swatchElements();
        if (!els.length) {
            return;
        }

        const selected = els.findIndex((el) => el.getAttribute('aria-pressed') === 'true');
        this.setActive(selected >= 0 ? selected : 0, els);
    }

    private setActive(index: number, els: HTMLButtonElement[]): void {
        els.forEach((el, i) => (el.tabIndex = i === index ? 0 : -1));
        this.activeIndex = index;
    }
}
